import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ORDER_URGENCY } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { DataTable } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import { SourceStateBadge } from './SourceStateBadge';
import { ConsultationFeeAlert, type ConsultationInvoiceRef } from './ConsultationFeeAlert';

interface Option {
  id: string;
  name: string;
}

interface VisitOrder {
  id: string;
  type: string;
  status: string;
  orderedAt: string;
  reason?: string | null;
  urgency?: string | null;
  externalTarget?: string | null;
  procedureNote?: string | null;
  items: { id?: string; label: string }[];
}

async function printLetter(encounterId: string, orderId: string) {
  const letter = await get<{ html: string }>(`/encounters/${encounterId}/print${qs({ letter: 'referral', orderId })}`);
  printDocument(letter.html);
}

export function VisitOrdersPanel({
  encounterId,
  patientId,
  readOnly,
  fee,
  only,
}: {
  encounterId: string;
  patientId: string;
  readOnly: boolean;
  fee?: { context: string; status: string; consultationInvoice?: ConsultationInvoiceRef | null };
  only?: 'referral_admission';
}) {
  const shownTypes = only ? ['referral', 'admission'] : ['referral', 'admission', 'procedure'];
  const { can, hasModule } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'internal' | 'external'>('internal');
  const [targetDoctorId, setTargetDoctorId] = useState('');
  const [targetDepartmentId, setTargetDepartmentId] = useState('');
  const [externalTarget, setExternalTarget] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [urgency, setUrgency] = useState<(typeof ORDER_URGENCY)[number]>('routine');
  const [reason, setReason] = useState('');
  const [sendNow, setSendNow] = useState(false);
  const [admissionReason, setAdmissionReason] = useState('');
  const [wardClass, setWardClass] = useState('');
  const [stayDays, setStayDays] = useState('');
  const [procedureId, setProcedureId] = useState('');
  const [completing, setCompleting] = useState<string | null>(null);
  const writable = !readOnly && can('order.write');

  const orders = useQuery({
    queryKey: ['visit-orders', encounterId, only ?? 'all'],
    queryFn: async () => (await get<{ data: VisitOrder[] }>(`/clinical-orders${qs({ encounterId, limit: 50 })}`)).data.filter((order) => shownTypes.includes(order.type)),
  });
  const doctors = useQuery({ queryKey: ['doctors'], queryFn: async () => (await get<{ data: Option[] }>('/org/clinicians?kind=doctors&limit=200')).data, enabled: writable });
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => get<Option[]>('/org/departments'), enabled: writable });
  const procedures = useQuery({
    queryKey: ['charge-items', 'procedure'],
    queryFn: async () => (await get<{ data: { id: string; code: string; name: string }[] }>('/charge-items?category=procedure&limit=300')).data,
    enabled: writable && !only,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['visit-orders', encounterId] });
    void queryClient.invalidateQueries({ queryKey: ['encounter', encounterId] });
    void queryClient.invalidateQueries({ queryKey: ['authorisation'] });
  };

  const refer = useMutation({
    mutationFn: () =>
      post<{ id: string; tokenNo: string | null }>('/clinical-orders/referral', {
        patientId,
        encounterId,
        kind,
        ...(kind === 'internal' ? { targetDoctorId: targetDoctorId || undefined, targetDepartmentId: targetDepartmentId || undefined, sendNow } : { externalTarget, specialty: specialty || undefined }),
        urgency,
        reason,
      }),
    onSuccess: async (order) => {
      toast.success(order.tokenNo ? tr('opd', 'sent_now_token_x', { tokenNo: order.tokenNo }) : tr('opd', 'referral_recorded'));
      setReason('');
      refresh();
      if (kind === 'external') await printLetter(encounterId, order.id);
    },
    onError: (error) => toast.error(tr('opd', 'could_not_record_the_referral'), describeError(error)),
  });

  const admit = useMutation({
    mutationFn: () =>
      post('/clinical-orders/admission-request', {
        patientId,
        encounterId,
        urgency,
        reason: admissionReason,
        wardClass: wardClass || undefined,
        estimatedStayDays: stayDays ? Number(stayDays) : undefined,
      }),
    onSuccess: () => {
      toast.success(tr('opd', 'admission_requested'), tr('opd', 'the_ward_sees_it_under_admissions'));
      setAdmissionReason('');
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_request_admission'), describeError(error)),
  });

  const procedure = useMutation({
    mutationFn: () => post('/clinical-orders/procedure', { patientId, encounterId, chargeItemId: procedureId }),
    onSuccess: () => {
      toast.success(tr('opd', 'procedure_ordered_and_added_to_the_bill'));
      setProcedureId('');
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_order_the_procedure'), describeError(error)),
  });

  const complete = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => post(`/clinical-orders/${id}/complete`, { note }),
    onSuccess: () => {
      setCompleting(null);
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_record_the_procedure'), describeError(error)),
  });

  return (
    <div className="mt-4 space-y-4">
      <DataTable
        rows={orders.data}
        loading={orders.isLoading} refreshing={orders.isFetching}
        error={orders.error}
        rowKey={(row) => row.id}
        empty={only ? tr('opd', 'order_no_referral_or_admission') : tr('opd', 'no_referral_admission_request_or_procedure_for')}
        columns={[
          { header: tr('opd', 'order'), cell: (row) => <Badge variant="secondary">{humanize(row.type)}</Badge> },
          {
            header: tr('opd', 'for'),
            cell: (row) => (
              <span className="inline-flex flex-wrap items-center gap-1">
                {row.items.map((item) => item.label).join(', ')}
                {row.type === 'procedure' && <SourceStateBadge sourceKeys={row.items.map((item) => (item.id ? `clinical_order_item:${item.id}` : null))} />}
              </span>
            ),
          },
          { header: tr('opd', 'when'), cell: (row) => dateTime(row.orderedAt) },
          { header: tr('opd', 'status'), cell: (row) => humanize(row.status) },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {row.type !== 'procedure' && (
                  <Button size="sm" variant="outline" onClick={() => void printLetter(encounterId, row.id)}>
                    {tr('opd', 'letter')}
                  </Button>
                )}
                {row.type === 'procedure' && row.status !== 'completed' && can('order.write', 'vitals.write') && (
                  <Button
                    size="sm"
                    onClick={() => setCompleting(row.id)}
                  >
                    {tr('opd', 'done')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      {completing && (
        <ReasonDialog
          title={tr('opd', 'procedure_note_title')}
          description={tr('opd', 'procedure_note_description')}
          label={tr('opd', 'procedure_note')}
          minLength={3}
          maxLength={4000}
          submitLabel={tr('opd', 'done')}
          loading={complete.isPending}
          onSubmit={(note) => complete.mutate({ id: completing, note })}
          onClose={() => setCompleting(null)}
        />
      )}

      {writable && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{tr('opd', 'refer')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ChoiceButtons
                label={tr('opd', 'refer')}
                value={kind}
                onChange={setKind}
                options={[
                  { value: 'internal', label: tr('opd', 'to_a_colleague_here') },
                  { value: 'external', label: tr('opd', 'to_another_hospital_or_specialist') },
                ]}
              />
              {kind === 'internal' ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={tr('opd', 'doctor')}>
                    <Select value={targetDoctorId} onChange={(e) => setTargetDoctorId(e.target.value)}>
                      <option value="">{tr('opd', 'any_in_the_department')}</option>
                      {doctors.data?.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>
                          {doctor.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={tr('opd', 'department')}>
                    <Select value={targetDepartmentId} onChange={(e) => setTargetDepartmentId(e.target.value)}>
                      <option value="">—</option>
                      {departments.data?.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={tr('opd', 'refer_to')} required>
                    <Input value={externalTarget} maxLength={240} onChange={(e) => setExternalTarget(e.target.value)} />
                  </Field>
                  <Field label={tr('opd', 'speciality')}>
                    <Input value={specialty} maxLength={120} onChange={(e) => setSpecialty(e.target.value)} />
                  </Field>
                </div>
              )}
              <Field label={tr('opd', 'urgency')}>
                <ChoiceButtons
                  value={urgency}
                  onChange={setUrgency}
                  options={ORDER_URGENCY.map((value) => ({ value, label: humanize(value) }))}
                />
              </Field>
              <Field label={tr('opd', 'reason')} required>
                <Textarea rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              {kind === 'internal' && targetDoctorId && (
                <Checkbox checked={sendNow} onCheckedChange={setSendNow} label={tr('opd', 'send_now_put_the_patient_in_the_colleagues')} />
              )}
              <Button onClick={() => refer.mutate()} loading={refer.isPending} disabled={reason.trim().length < 3 || (kind === 'external' ? !externalTarget.trim() : !targetDoctorId && !targetDepartmentId)}>
                {tr('opd', 'refer')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{hasModule('ipd') ? tr('opd', 'admission') : tr('opd', 'advise_admission')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {hasModule('ipd') ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label={tr('opd', 'ward_class')}>
                      <Select value={wardClass} onChange={(e) => setWardClass(e.target.value)}>
                        <option value="">{tr('opd', 'any')}</option>
                        {['general', 'semi_private', 'private', 'icu', 'hdu', 'isolation'].map((value) => (
                          <option key={value} value={value}>
                            {humanize(value, 'bed_class')}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={tr('opd', 'expected_stay_days')}>
                      <Input type="number" min={1} max={365} value={stayDays} onChange={(e) => setStayDays(e.target.value)} />
                    </Field>
                  </div>
                  <Field label={tr('opd', 'reason')} required>
                    <Textarea rows={2} maxLength={1000} value={admissionReason} onChange={(e) => setAdmissionReason(e.target.value)} />
                  </Field>
                  <Button onClick={() => admit.mutate()} loading={admit.isPending} disabled={admissionReason.trim().length < 3}>
                    {tr('opd', 'request_admission')}
                  </Button>
                </>
              ) : (
                <Alert>
                  {tr('opd', 'this_hospital_has_no_wards_here_refer_the')}
                </Alert>
              )}
            </CardContent>
          </Card>

          {!only && (procedures.data?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{tr('opd', 'procedure')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-end gap-2">
                {fee && <ConsultationFeeAlert encounter={{ ...fee, id: encounterId }} className="w-full" />}
                <Field label={tr('opd', 'procedure')} className="min-w-[16rem] flex-1">
                  <Select value={procedureId} onChange={(e) => setProcedureId(e.target.value)}>
                    <option value="">{tr('opd', 'choose')}</option>
                    {procedures.data?.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.code} — {item.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Button onClick={() => procedure.mutate()} loading={procedure.isPending} disabled={!procedureId}>
                  {tr('opd', 'order')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

interface ReferralRow {
  id: string;
  orderedAt: string;
  urgency: string | null;
  reason: string | null;
  targetAppointmentId: string | null;
  patient: { id: string; mrn: string; fullName: string; phone: string | null };
}

export function ReferralsCard({ scope }: { scope: 'mine' | 'all' }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const referrals = useQuery({
    queryKey: ['referrals', scope],
    queryFn: () => get<{ data: ReferralRow[]; nextCursor: string | null }>(`/clinical-orders/referrals${qs({ scope })}`),
  });
  const close = useMutation({
    mutationFn: (id: string) => post(`/clinical-orders/${id}/close-referral`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['referrals'] }),
    onError: (error) => toast.error(tr('opd', 'could_not_close_the_referral'), describeError(error)),
  });
  if (!referrals.data?.data.length) return null;
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-sm">{scope === 'mine' ? tr('opd', 'referred_to_me') : tr('opd', 'referrals_to_book')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {referrals.data.data.map((row) => (
          <div key={row.id} className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>{row.patient.fullName}</strong> <span className="text-xs text-muted-foreground">{row.patient.mrn}{row.patient.phone ? ` · ${row.patient.phone}` : ''}</span>
              {row.urgency && row.urgency !== 'routine' && <Badge variant="destructive" className="ms-2">{humanize(row.urgency)}</Badge>}
              <span className="block text-xs text-muted-foreground">{row.reason}</span>
            </span>
            <Button size="sm" variant="outline" onClick={() => close.mutate(row.id)}>
              {scope === 'mine' ? tr('opd', 'seen') : tr('opd', 'booked')}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
